import React, { useEffect, useState } from "react";
import { Comments, InfoBox } from "../../components";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import EditableNotes from "../../components/admin/EditableNotes";
import { jwtDecode } from "jwt-decode";
import {
  IconPhone,
  IconMail,
  IconAlertCircle,
  IconLoader,
  IconBuildingSkyscraper,
} from "@tabler/icons-react";

const UniversityApplicationProcess = () => {
  const [blockAccess, setBlockAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const isTableFullScreen = useSelector(
    (state) => state.application.isTableFullScreen
  );
  const [userRole, setUserRole] = useState();

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
    setLoading(false);
  }, []);

  const studentDetails = useSelector((state) => state.user.userDetails);

  useEffect(() => {
    const isFinalised =
      studentDetails?.is_finalised ?? studentDetails?.isfinalised;
    if (
      !isFinalised &&
      !studentDetails?.is_show_table_to_student
    ) {
      setBlockAccess(true);
    } else {
      setBlockAccess(false);
    }
    setLoading(false);
  }, [studentDetails]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <IconLoader
            size={48}
            className="mx-auto animate-spin text-purple-600"
          />
          <p className="mt-4 text-gray-600 font-medium">
            Loading university application details...
          </p>
        </div>
      </div>
    );
  }

  if (blockAccess) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="bg-red-50 text-red-600 p-8 rounded-lg shadow-lg text-center max-w-md animate-fade-in">
          <IconAlertCircle size={48} className="mx-auto mb-4" stroke={1.5} />
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-red-700">
            You are locked from viewing this page. Please contact your counselor
            for more information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div
        className={`${isTableFullScreen
            ? "fixed inset-0 z-50 bg-black bg-opacity-50"
            : "p-4"
          } flex justify-center transition-all duration-300 ease-in-out`}
        style={{ pointerEvents: isTableFullScreen ? "none" : "auto" }}
      >
        <div
          className={`max-w-7xl w-full mx-auto ${isTableFullScreen ? "items-center" : ""
            } transition-all duration-300 ease-in-out`}
          style={{ pointerEvents: "auto" }}
        >
          {/* Main Application Container */}
          <div className="flex flex-col gap-6">
            {/* University Application Table */}
            <div
              className={`relative ${isTableFullScreen
                  ? "w-screen h-screen"
                  : "w-full h-[700px] md:h-[640px]"
                } bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden`}
            >
              <Outlet />
            </div>

            {/* Editable Notes Section */}
            {!isTableFullScreen && (
              <div className="bg-white rounded-lg shadow-md border border-gray-200">
                <EditableNotes
                  studentId={studentDetails.student_id}
                  studentDetails={studentDetails}
                />
              </div>
            )}

            {/* Two-column layout for Contact and Comments */}
            {!isTableFullScreen && (
              <div className="flex flex-wrap gap-4">
                {/* POC Card - Smaller width */}
                <div className="min-w-[250px] w-full md:w-1/4  flex-shrink-0">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 h-full">
                    <div className="bg-purple-600 p-3">
                      <div className="flex items-center justify-center">
                        <IconBuildingSkyscraper
                          className="text-white mr-2"
                          size={20}
                          stroke={1.5}
                        />
                        <h2 className="text-white text-lg font-semibold">
                          Application POC
                        </h2>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Avatar and name section */}
                      <div className="flex items-center mb-3">
                        <div className="relative mr-3">
                          {studentDetails.assigned_admins
                            ?.assigned_application_manager ? (
                            <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold bg-green-500">
                              {(
                                studentDetails.assigned_admins
                                  .assigned_application_manager.username || "UK"
                              )
                                .split(" ")
                                .map((word) => word[0])
                                .join("")
                                .toUpperCase()
                                .substring(0, 2)}
                              {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div> */}
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold bg-gray-500">
                              NA
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {studentDetails.assigned_admins
                              ?.assigned_application_manager?.username ||
                              "Not Assigned"}
                          </p>
                          <p className="text-xs text-gray-500">
                            Application Manager
                          </p>
                        </div>
                      </div>

                      {/* Contact info */}
                      <div className="space-y-2 mt-2">
                        <div className="flex items-center text-gray-700">
                          <IconPhone
                            size={16}
                            className="text-gray-400 mr-2"
                            stroke={1.5}
                          />
                          <span className="text-sm text-gray-600">
                            {studentDetails.assigned_admins
                              ?.assigned_application_manager?.phone ||
                              "Not Available"}
                          </span>
                        </div>
                        <div className="flex items-start text-gray-700">
                          <IconMail
                            size={16}
                            className="text-gray-400 mr-2 mt-1"
                            stroke={1.5}
                          />
                          <span className="text-sm text-gray-600 break-all">
                            {studentDetails.assigned_admins
                              ?.assigned_application_manager?.email ||
                              "Not Available"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comments Section - Takes up more space */}
                <div className="flex-grow">
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 h-full">
                    <Comments enableUniversityTagging={true} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add a style for fade-in animation */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default UniversityApplicationProcess;
